/**
 * JitsLab API adapter layer.
 *
 * All API calls go through here so that:
 * 1. Screens don't import fetch directly — they call typed functions
 * 2. Auth headers, error handling, and base URL are centralized
 * 3. Subagents building screens have a clear interface contract
 *
 * The backend is unchanged: functions/[[all]].ts on Cloudflare Pages.
 * In dev, Vite proxies /api to localhost:8788 (wrangler pages dev).
 * In prod, requests go to the same origin.
 */

const API_BASE = import.meta.env.VITE_API_BASE ?? '';

// ── Auth ──────────────────────────────────────────────────────────
export function getAuthToken(): string | null {
  return localStorage.getItem('jl_token');
}

export function setAuthToken(token: string): void {
  localStorage.setItem('jl_token', token);
}

export function clearAuthToken(): void {
  localStorage.removeItem('jl_token');
}

// ── Fetch wrapper ──────────────────────────────────────────────────
async function apiFetch<T>(
  path: string,
  options?: RequestInit & { skipAuth?: boolean }
): Promise<T> {
  const headers = new Headers(options?.headers);
  headers.set('Content-Type', 'application/json');

  if (!options?.skipAuth) {
    const token = getAuthToken();
    if (token) headers.set('Authorization', `Bearer ${token}`);
  }

  const res = await fetch(`${API_BASE}${path}`, { ...options, headers });

  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new ApiError(res.status, res.statusText, body);
  }

  // 204 No Content
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public statusText: string,
    public body: string
  ) {
    super(`API ${status}: ${statusText}`);
    this.name = 'ApiError';
  }
}

// ── Typed API endpoints (screen subsets) ──────────────────────────
// Each screen module imports only what it needs. Subagents get a clear
// contract: function name, args, return type.

// — Sessions —
export interface Session {
  id: number;
  date: string;
  duration_minutes: number;
  gi_type: string; // 'gi' | 'nogi' | 'both'
  start_time?: string;
  end_time?: string;
  notes?: string;
  /** Backend GET /api/sessions returns this (legacy app.js renders it in recent list) */
  training_focus?: string | null;
  location?: string | null;
  techniques?: string[];
  /** GET returns names/ids; POST accepts rich objects (see SessionPayload) */
  partners?: Array<string | { name: string; belt?: string | null; note?: string | null }>;
  // Write-side fields the backend accepts on create/update (see app.js submitSession)
  raw_notes?: string | null;
  is_draft?: boolean;
  node_ids?: string[];
  user_tag_ids?: string[];
  technique_notes?: Record<string, string>;
  focus_item_ids?: { id: string | number; progress?: string; note?: string }[];
}

/** POST /api/sessions body — documents the fields beyond the GET shape. */
export interface SessionPayload {
  date: string;
  duration_mins?: number | null;
  gi_type?: string;
  notes?: string;
  raw_notes?: string | null;
  is_draft?: boolean;
  node_ids?: string[];
  user_tag_ids?: string[];
  technique_notes?: Record<string, string>;
  partners?: { name: string; belt?: string | null; note?: string | null }[];
  focus_item_ids?: { id: string | number; progress?: string; note?: string }[];
}

// — User profile (GET/PUT /api/profile) —
// The brief contract is { name, email, belt, weeklyTarget }; the backend row
// carries more fields, so getUser() normalizes the wire shape.
export interface UserProfile {
  id: string;
  name: string;
  email: string;
  belt: string;
  weeklyTarget: number; // sessions_per_week_target
  email_verified?: boolean;
}

// — Achievements (GET /api/achievements → { achievements }) —
// Backend rows: { id, name, icon, unlocked_at, unlocked, tier, ladder,
// section, description, locked_description }. The brief contract is
// { id, title, icon, earned_at?, locked } — normalize here.
export interface Achievement {
  id: string;
  title: string;
  icon: string;
  earned_at?: string | null;
  locked: boolean;
  description?: string;
  locked_description?: string;
  tier?: number;
  ladder?: string | null;
  section?: string;
}

// — Digest settings (GET/PUT /api/settings/digest) —
export interface DigestSettings {
  frequency: number; // 0 = off, >0 = days between digests
  last_sent_at?: string | null;
  next_digest?: string | null;
}

// — Partners —
export interface Partner {
  key: string;
  name: string;
  belt: string;
  session_count: number;
}

// — Goals (backend: focus_items with category='goal') —
export interface Goal {
  id: string; // UUID string (backend focus_items.id), NOT a number
  name: string; // the goal title
  description?: string | null;
  objectives?: string | null; // free-text notes shown on the goal card
  target_reps?: number | null;
  target_date?: string | null; // YYYY-MM-DD
  status?: string; // 'active' | 'achieved' | 'dormant'
  category?: string | null;
  parent_id?: string | null;
  technique_ids?: string[];
  user_tag_ids?: string[];
  session_count?: number;
  last_worked?: string | null;
  consistency?: number;
  progress_pct?: number | null;
  created_at?: string;
}

/** POST /api/focus body for creating a goal. */
export interface GoalPayload {
  name: string;
  description?: string | null;
  objectives?: string | null;
  target_reps?: number | null;
  target_date?: string | null;
  status?: string;
  category?: 'goal';
  parent_id?: string | null;
  technique_ids?: string[];
}

export interface ProcessNotesResponse {
  processed?: string;
  segments?: { technique: string; details?: string[] }[];
  error?: string;
}

export interface TranscribeResponse {
  transcript?: string;
  word_count?: number;
  error?: string;
}

// — Dashboard (GET /api/stats — there is no /api/dashboard route) —
export interface DashboardData {
  totalSessions: number;
  totalMinutes: number;
  sessionsThisWeek: number;
  weekTarget: number;
  sessionsThisMonth: number;
  monthTarget: number;
  /** Backend /api/stats does not return streak — screens compute it from heatmap when absent. */
  streak?: number;
  heatmap: Array<{ date: string; count: number }>;
  topTags: Array<{ id: string; name: string; type?: string; count: number; node_id?: string }>;
  recentSessions?: Session[];
  achievementCount: number;
  goalsCompleted: number;
  coachSummary?: string;
}

// — Coach summary (GET /api/coach/summary) —
export interface CoachSummaryResponse {
  summary: string | null;
  session_count?: number;
  canUseCoach?: boolean;
  generated_at?: string;
  cached?: boolean;
}

// — Stats (Wave 6) — merged from the 4 /api/stats/* endpoints.
// The brief's StatsData contract maps onto functions/[[all]].ts lines 2846-2990;
// getStats() does the merge so screens only call api.getStats() once.
// Note: the backend has no `trainingFocus` enum aggregate — `focusBreakdown`
// comes from /api/stats `topFoci` (top focus_items by session frequency).
export interface StatsData {
  totalSessions: number;
  totalHours: number;
  avgDuration: number; // minutes
  giSessions: number;
  nogiSessions: number;
  beltDist: Record<string, number>;
  monthlyTrend: Array<{ month: string; count: number }>;
  topTechniques: Array<{ name: string; count: number; badge?: string }>;
  focusBreakdown: Record<string, number>;
  partnerStats: Array<{ partner: string; sessions: number; belt?: string }>;
}

// Wire shapes for the four /api/stats/* endpoints (backend is source of truth).
interface StatsWire {
  totalSessions?: number;
  totalMinutes?: number;
  volumeMonthly?: Array<{ month: string; session_count: number }>;
  giTypeMix?: Array<{ gi_type: string; count: number }>;
  topFoci?: Array<{ focus: string; count: number }>;
}
interface StatsTagsWire {
  tags?: Array<{ name: string; category?: string; session_count?: number }>;
}
interface StatsPartnersWire {
  partners?: Array<{ name: string; partner_belt?: string | null; session_count?: number }>;
}
interface StatsGymsWire {
  beltDistribution?: Array<{ belt?: string; count?: number }>;
}

// — Gameplans (backend: focus_items with category='gameplan') —
export interface GameplanItem {
  id: string;
  name: string;
  description?: string | null;
  status?: string;
  technique_ids?: string[];
  created_at?: string;
}

export interface TaxonomyNode {
  id: string;
  name: string;
  type?: string;
  category?: string;
  /** JSON string or array on the wire — normalized to array by screens */
  aliases?: string[] | string | null;
  description?: string | null;
  parent_id?: string | null;
  depth?: number;
  path?: string;
  gi_flag?: number | null;
  /** Present only on /api/taxonomy/tree nodes (backend builds the tree) */
  children?: TaxonomyNode[];
}

export interface Gameplan {
  id: string;
  title: string;
  created_at: string;
  description?: string | null;
  status?: string;
  techniques: Array<{ id: string; name: string; badge?: string }>;
}

export interface GameplanPayload {
  name: string;
  description?: string;
  technique_ids?: string[];
  /** Accepted by POST /api/focus (focus_items columns) — used by Onboarding goals */
  target_reps?: number | null;
  target_date?: string | null;
}

/** Normalize a /api/profile row (backend column names) into the UserProfile contract. */
function normalizeProfile(row: Record<string, unknown>): UserProfile {
  const weekly =
    row.sessions_per_week_target != null
      ? Number(row.sessions_per_week_target)
      : 4; // legacy default
  return {
    id: String(row.id ?? ''),
    name: String(row.name ?? ''),
    email: String(row.email ?? ''),
    belt: String(row.belt ?? ''),
    weeklyTarget: Number.isFinite(weekly) && weekly > 0 ? weekly : 4,
    email_verified: row.email_verified != null ? Boolean(row.email_verified) : undefined,
  };
}

// — Instructionals (Pro-tier video library, functions/[[all]].ts lines 903-1170) —
// Note: the list endpoint returns rows without hydrated technique_tags (the SQL
// is SELECT i.*, so the column is absent); the detail endpoint
// (getInstructionalById) hydrates them as { id, name }[] — hence the union.
export interface Instructional {
  id: number | string;
  title: string;
  source_url?: string;
  thumbnail_url?: string;
  video_url?: string;
  technique_tags?: Array<string | { id: string; name: string }>;
  note_count?: number;
  technique_count?: number;
  last_note_at?: string;
}

export interface InstructionalNote {
  id: number | string;
  body: string;
  /** Wire rows carry 0/1 — screens coerce with Boolean() */
  is_key: boolean;
  /** Array of taxonomy node ids (backend JSON-parses the column) */
  technique_tags?: string[];
  timestamp_start?: number | null;
  created_at: string;
}

export interface InstructionalDetail {
  instructional: Instructional;
  notes: InstructionalNote[];
}

// — Pulse (community insights) —
export interface PulseTag {
  id: string;
  name: string;
  has_summary: boolean;
  contributor_count?: number;
  note_count?: number;
  summary?: string;
}

export interface PulseSummary {
  text: string;
  contributor_count: number;
  note_count: number;
  generated_at: string;
  version: number;
}

/** GET /api/pulse/tags/:id/summary — k-anonymity-gated tag detail. */
export interface PulseTagDetail {
  tag: { id: string; name: string; popularity_count?: number };
  summary: PulseSummary | null;
  below_anonymity_floor?: boolean;
  contributor_count_hint?: number | null;
}

// — Health dashboard —
export interface HealthWorkout {
  date?: string;
  source_platform?: string;
  calories_kcal?: number;
  duration_minutes?: number;
  avg_heart_rate?: number;
  max_heart_rate?: number;
}
export interface HealthDashboard {
  workouts_per_day?: HealthWorkout[];
}

// — Cloze (spaced repetition) —
// Wire shape from functions/cloze/review.ts getDueCards() (backend is truth —
// the brief's { id, front, back, technique_tag } does not match the API).
// Cards carry group_id/group_label/note_text/span_texts; screens derive the
// display front (note_text with spans blanked) and back (span_texts joined).
export interface ClozeCard {
  group_id: number;
  note_id?: string;
  group_label: string;
  span_ids?: number[];
  note_text: string;
  span_texts: string[];
  due_at?: string;
  interval_days?: number;
  ease_factor?: number;
  edit_drift?: boolean;
}
export interface ClozeStats {
  due?: number;
  total?: number;
  new_cards?: number;
}

// — Subscriptions —
export interface SubscriptionStatus {
  tier?: string;
  paid_until?: string;
  cancel_at_period_end?: boolean;
  trial_end?: string;
}

export const api = {
  // Sessions (backend returns { sessions, nextCursor } — unwrap here)
  getSessions: async (
    limit = 50,
    filters?: { date_from?: string; date_to?: string }
  ): Promise<Session[]> => {
    const params = new URLSearchParams({ limit: String(limit) });
    if (filters?.date_from) params.set('date_from', filters.date_from);
    if (filters?.date_to) params.set('date_to', filters.date_to);
    const data = await apiFetch<{ sessions?: Session[] }>(`/api/sessions?${params.toString()}`);
    return data.sessions ?? [];
  },
  getDaySessions: async (date: string): Promise<Session[]> => {
    const data = await apiFetch<{ sessions?: Session[] }>(
      `/api/sessions?date_from=${date}&date_to=${date}&limit=10`
    );
    return data.sessions ?? [];
  },
  getSession: (id: string | number): Promise<Session> =>
    apiFetch(`/api/sessions/${id}`),
  createSession: (data: Partial<Session> & Partial<SessionPayload>): Promise<Session> =>
    apiFetch('/api/sessions', { method: 'POST', body: JSON.stringify(data) }),
  updateSession: (id: string | number, data: Partial<Session> & Partial<SessionPayload>): Promise<Session> =>
    apiFetch(`/api/sessions/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteSession: (id: string | number): Promise<void> =>
    apiFetch(`/api/sessions/${id}`, { method: 'DELETE' }),

  // Techniques (taxonomy)
  getTechniques: (): Promise<unknown[]> => apiFetch('/api/taxonomy'),
  getTechnique: (nodeId: string): Promise<unknown> => apiFetch(`/api/taxonomy/${nodeId}`),
  // Techniques (taxonomy) — real endpoints (functions/[[all]].ts lines 1797-1845).
  // /api/taxonomy/tree returns { tree, nodes }: nested tree + flat list.
  getTechniqueTree: async (): Promise<{ nodes: TaxonomyNode[]; tree: TaxonomyNode[] }> => {
    const data = await apiFetch<{ nodes?: TaxonomyNode[]; tree?: TaxonomyNode[] }>(
      '/api/taxonomy/tree'
    );
    return { nodes: data.nodes ?? [], tree: data.tree ?? [] };
  },
  searchTechniques: (q: string): Promise<{ nodes: TaxonomyNode[] }> =>
    apiFetch(`/api/taxonomy/search?q=${encodeURIComponent(q)}`),
  // NOTE: the backend has NO taxonomy write routes yet — functions/[[all]].ts is
  // read-only for taxonomy_nodes (verified 2026-08-04; unmatched /api/* returns
  // 404 JSON). These two are wired to the conventional REST paths so the
  // Techniques screen can attempt them; until a backend route lands they 404
  // and the screen falls back to a local-only mutation with a visible note.
  createTaxonomyNode: (data: { name: string; parent_id?: string | null }): Promise<unknown> =>
    apiFetch('/api/taxonomy', { method: 'POST', body: JSON.stringify(data) }),
  deleteTaxonomyNode: (nodeId: string): Promise<unknown> =>
    apiFetch(`/api/taxonomy/${encodeURIComponent(nodeId)}`, { method: 'DELETE' }),

  // Partners
  getPartners: (): Promise<Partner[]> => apiFetch('/api/partners'),
  createPartner: (data: { name: string; belt?: string | null }): Promise<{ success: boolean; id?: string }> =>
    apiFetch('/api/partners', { method: 'POST', body: JSON.stringify(data) }),
  deletePartner: (key: string): Promise<{ success: boolean }> =>
    apiFetch(`/api/partners/${key}`, { method: 'DELETE' }),

  // Dashboard / stats — GET /api/stats is the real dashboard endpoint
  // (the legacy app.js loadDashboard fetches it; /api/dashboard does not exist)
  getDashboard: (): Promise<DashboardData> => apiFetch('/api/stats'),
  // Stats — merges the 4 /api/stats/* endpoints into the StatsData contract.
  // /api/stats is primary (rejects → screen retry); the breakdown endpoints are
  // best-effort and degrade to empty arrays (legacy used per-endpoint fallbacks).
  getStats: async (): Promise<StatsData> => {
    const [stats, tags, partners, gyms] = await Promise.all([
      apiFetch<StatsWire>('/api/stats'),
      apiFetch<StatsTagsWire>('/api/stats/tags').catch(() => null),
      apiFetch<StatsPartnersWire>('/api/stats/partners').catch(() => null),
      apiFetch<StatsGymsWire>('/api/stats/gyms').catch(() => null),
    ]);
    const totalSessions = stats?.totalSessions ?? 0;
    const totalMinutes = stats?.totalMinutes ?? 0;
    // 'both' sessions count toward both sides of the Gi/No-Gi split
    const giMix = stats?.giTypeMix ?? [];
    const giSessions = giMix
      .filter((g) => g.gi_type === 'gi' || g.gi_type === 'both')
      .reduce((a, g) => a + (g.count ?? 0), 0);
    const nogiSessions = giMix
      .filter((g) => g.gi_type === 'nogi' || g.gi_type === 'both')
      .reduce((a, g) => a + (g.count ?? 0), 0);
    const beltDist: Record<string, number> = {};
    for (const b of gyms?.beltDistribution ?? []) {
      if (b.belt) beltDist[b.belt.toLowerCase()] = (beltDist[b.belt.toLowerCase()] ?? 0) + (b.count ?? 0);
    }
    const monthlyTrend = (stats?.volumeMonthly ?? [])
      .slice(-6)
      .map((v) => ({ month: v.month, count: v.session_count ?? 0 }));
    const topTechniques = (tags?.tags ?? [])
      .filter((t) => (t.session_count ?? 0) > 0)
      .sort((a, b) => (b.session_count ?? 0) - (a.session_count ?? 0))
      .slice(0, 10)
      .map((t) => ({ name: t.name, count: t.session_count ?? 0, badge: t.category }));
    const focusBreakdown: Record<string, number> = {};
    for (const f of stats?.topFoci ?? []) {
      if (f.focus) focusBreakdown[f.focus] = (focusBreakdown[f.focus] ?? 0) + (f.count ?? 0);
    }
    const partnerStats = (partners?.partners ?? [])
      .slice(0, 5)
      .map((p) => ({ partner: p.name, sessions: p.session_count ?? 0, belt: p.partner_belt ?? undefined }));
    return {
      totalSessions,
      totalHours: Math.round(totalMinutes / 60),
      avgDuration: totalSessions > 0 ? Math.round(totalMinutes / totalSessions) : 0,
      giSessions,
      nogiSessions,
      beltDist,
      monthlyTrend,
      topTechniques,
      focusBreakdown,
      partnerStats,
    };
  },
  getCoachSummary: (): Promise<CoachSummaryResponse> => apiFetch('/api/coach/summary'),

  // User profile — GET/PUT /api/profile (backend row → UserProfile contract)
  getUser: async (): Promise<UserProfile> => {
    const row = await apiFetch<Record<string, unknown>>('/api/profile');
    return normalizeProfile(row);
  },
  updateProfile: async (fields: Partial<UserProfile> & {
    sessions_per_week_target?: number;
    // Backend row fields (functions/[[all]].ts PUT /api/profile allowed list)
    training_start_date?: string | null;
    blue_belt_date?: string | null;
    purple_belt_date?: string | null;
    brown_belt_date?: string | null;
    black_belt_date?: string | null;
  }): Promise<UserProfile> => {
    const row = await apiFetch<Record<string, unknown>>('/api/profile', {
      method: 'PUT',
      body: JSON.stringify(fields),
    });
    return normalizeProfile(row);
  },

  // Achievements — GET /api/achievements → { achievements } (unwrap + normalize)
  getAchievements: async (): Promise<Achievement[]> => {
    const data = await apiFetch<{ achievements?: Achievement[] }>('/api/achievements');
    const list = Array.isArray(data) ? data : data?.achievements ?? [];
    return list.map((a) => {
      const r = a as unknown as Record<string, unknown>;
      return {
        id: String(r.id ?? ''),
        title: String(r.title ?? r.name ?? ''),
        icon: String(r.icon ?? '🏆'),
        earned_at: (r.earned_at ?? r.unlocked_at) as string | null | undefined,
        locked: Boolean(r.locked ?? (r.unlocked === undefined ? true : !r.unlocked)),
        description: r.description ? String(r.description) : undefined,
        locked_description: r.locked_description ? String(r.locked_description) : undefined,
        tier: r.tier != null ? Number(r.tier) : undefined,
        ladder: r.ladder != null ? String(r.ladder) : undefined,
        section: r.section ? String(r.section) : undefined,
      };
    });
  },
  // POST /api/achievements/check — evaluates rules, returns newly unlocked.
  // Legacy app.js calls this before rendering the awards page.
  checkAchievements: (): Promise<{ unlocked: unknown[] }> =>
    apiFetch('/api/achievements/check', { method: 'POST' }),

  // Digest settings — GET/PUT /api/settings/digest (frequency: 0 = off)
  getDigestSettings: (): Promise<DigestSettings> => apiFetch('/api/settings/digest'),
  setDigest: (frequency: number): Promise<{ success: boolean }> =>
    apiFetch('/api/settings/digest', {
      method: 'PUT',
      body: JSON.stringify({ frequency }),
    }),

  // Sign out — revoke server session, then clearAuthToken() on the client
  logout: (): Promise<unknown> =>
    apiFetch('/api/auth/logout', { method: 'POST', skipAuth: true }).catch(() => null),

  // Goals — backend stores these as focus_items with category='goal'
  // (functions/[[all]].ts GET/POST /api/focus, PUT/DELETE /api/focus/:id).
  // The client unwraps { focus_items } so screens get a plain Goal[].
  getGoals: async (): Promise<Goal[]> => {
    const res = await apiFetch<{ focus_items: Goal[] }>('/api/focus?category=goal');
    return res.focus_items ?? [];
  },
  createGoal: (data: GoalPayload): Promise<{ id: string; success: boolean }> =>
    apiFetch('/api/focus', { method: 'POST', body: JSON.stringify(data) }),
  updateGoal: (id: string, data: Partial<GoalPayload>): Promise<unknown> =>
    apiFetch(`/api/focus/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteGoal: (id: string): Promise<unknown> =>
    apiFetch(`/api/focus/${id}`, { method: 'DELETE' }),

  // Coach notes AI processing (Clean / Organize / Recap)
  processNotes: (
    text: string,
    mode: 'clean' | 'organize' | 'segment'
  ): Promise<ProcessNotesResponse> =>
    apiFetch('/api/process-notes', { method: 'POST', body: JSON.stringify({ text, mode }) }),

  // Voice transcription (base64 WAV, as app.js sends it)
  transcribe: (audio: string, mimeType: string): Promise<TranscribeResponse> =>
    apiFetch('/api/transcribe', { method: 'POST', body: JSON.stringify({ audio, mimeType }) }),

  // Health Connect — queue a saved session for the APK to sync on next launch
  queueHcSync: (sessionId: number): Promise<unknown> =>
    apiFetch('/api/hc/queue-sync', {
      method: 'POST',
      body: JSON.stringify({ session_id: sessionId }),
    }),

  // Gameplans — backend stores these as focus_items with category='gameplan'
  // (functions/[[all]].ts GET /api/focus?category=gameplan, POST /api/focus,
  //  DELETE /api/focus/:id). There is NO /api/gameplans route.
  // getGameplans resolves technique_ids → { id, name, badge } by joining the
  // taxonomy list (/api/tags → { nodes }). If the taxonomy fetch fails the
  // technique ids are returned as names (never throws for that reason).
  getGameplans: async (): Promise<Gameplan[]> => {
    const [focusRes, tagsRes] = await Promise.all([
      apiFetch<{ focus_items?: GameplanItem[] }>('/api/focus?category=gameplan'),
      apiFetch<{ nodes?: TaxonomyNode[] }>('/api/tags').catch(() => null),
    ]);
    const nodeMap = new Map((tagsRes?.nodes ?? []).map((n) => [n.id, n]));
    const items = Array.isArray(focusRes) ? focusRes : focusRes?.focus_items ?? [];
    return items.map((item) => ({
      id: item.id,
      title: item.name,
      created_at: item.created_at ?? '',
      description: item.description ?? null,
      status: item.status ?? 'active',
      techniques: (item.technique_ids ?? []).map((tid: string) => {
        const node = nodeMap.get(tid);
        return { id: tid, name: node?.name ?? tid, badge: node?.type };
      }),
    }));
  },
  createGameplan: (data: GameplanPayload): Promise<{ id: string; success: boolean }> =>
    apiFetch('/api/focus', {
      method: 'POST',
      body: JSON.stringify({ ...data, category: 'gameplan' }),
    }),
  deleteGameplan: (id: string): Promise<{ success: boolean }> =>
    apiFetch(`/api/focus/${id}`, { method: 'DELETE' }),
  updateGameplan: (id: string, data: Partial<GameplanPayload>): Promise<{ success: boolean }> =>
    apiFetch(`/api/focus/${id}`, { method: 'PUT', body: JSON.stringify(data) }),

  // Instructionals — Pro-tier video library (functions/[[all]].ts lines 903-1170).
  // Every route 403s with { error: 'This feature requires Pro subscription',
  // requires_pro: true } for non-Pro accounts. IDs are numeric on the wire
  // (regex \d+), but the contract allows string for future-proofing.
  getInstructionals: async (
    type: 'library' | 'mine',
    cursor?: string
  ): Promise<{ items: Instructional[]; nextCursor: string | null }> => {
    const params = new URLSearchParams({ type, limit: '20' });
    if (cursor) params.set('cursor', cursor);
    return apiFetch(`/api/instructionals?${params.toString()}`);
  },
  createInstructional: (source_url: string): Promise<{ id: string; title: string; thumbnail_url: string }> =>
    apiFetch('/api/instructionals', { method: 'POST', body: JSON.stringify({ source_url }) }),
  updateInstructional: (
    id: string | number,
    data: { technique_tags?: string[]; title?: string }
  ): Promise<{ success: boolean }> =>
    apiFetch(`/api/instructionals/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteInstructional: (id: string | number): Promise<void> =>
    apiFetch(`/api/instructionals/${id}`, { method: 'DELETE' }),
  getInstructionalDetail: (id: string | number): Promise<InstructionalDetail> =>
    apiFetch(`/api/instructionals/${id}/notes`),
  createInstructionalNote: (
    instId: string | number,
    data: { body: string; technique_tags?: string[]; is_key?: boolean; timestamp_start?: number | null }
  ): Promise<{ id: string }> =>
    apiFetch(`/api/instructionals/${instId}/notes`, { method: 'POST', body: JSON.stringify(data) }),
  updateNote: (id: string | number, data: { body?: string; is_key?: boolean }): Promise<{ success: boolean }> =>
    apiFetch(`/api/notes/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteNote: (id: string | number): Promise<void> =>
    apiFetch(`/api/notes/${id}`, { method: 'DELETE' }),

  // Pulse — community insights (functions/[[all]].ts)
  getPulseStatus: (): Promise<{ contribute_to_pulse: boolean }> =>
    apiFetch('/api/settings/pulse'),
  setPulseStatus: (contribute: boolean): Promise<{ success: boolean }> =>
    apiFetch('/api/settings/pulse', { method: 'PUT', body: JSON.stringify({ contribute_to_pulse: contribute }) }),
  getPulseTags: (query?: string, limit = 50): Promise<{ tags: PulseTag[] }> => {
    const url = query && query.length >= 2
      ? `/api/pulse/tags/search?q=${encodeURIComponent(query)}&limit=${limit}`
      : `/api/pulse/tags/popular?limit=${limit}`;
    return apiFetch(url);
  },
  // Real route is /api/pulse/tags/:id/summary (functions/[[all]].ts line 4669) —
  // the bare /api/pulse/tags/:id path 404s.
  getPulseTagDetail: (tagId: string): Promise<PulseTagDetail> =>
    apiFetch(`/api/pulse/tags/${encodeURIComponent(tagId)}/summary`),
  generatePulse: (): Promise<{ success: boolean }> =>
    apiFetch('/api/pulse/generate', { method: 'POST' }),

  // Health — wearable dashboard (functions/[[all]].ts)
  getHealthDashboard: (userId: string, from: string, to: string): Promise<HealthDashboard> =>
    apiFetch(`/api/users/${userId}/health-dashboard?from=${from}&to=${to}`),
  syncToHealthConnect: (): Promise<{ success: boolean }> =>
    apiFetch('/api/health/sync', { method: 'POST' }),

  // Cloze — spaced repetition (functions/[[all]].ts lines 3811-3908).
  // NOTE: the grade route is POST /api/cloze/grade with { group_id, grade }
  // (0=again, 1=hard, 2=good, 3=easy) — there is NO /api/cloze/rate route.
  getClozeQueue: (): Promise<{ cards: ClozeCard[]; stats: ClozeStats }> =>
    apiFetch('/api/cloze/queue'),
  rateClozeCard: (cardId: string | number, rating: number): Promise<{ success: boolean }> =>
    apiFetch('/api/cloze/grade', { method: 'POST', body: JSON.stringify({ group_id: cardId, grade: rating }) }),
  getTargetedCloze: (prompt: string): Promise<{ cards: ClozeCard[]; stats: ClozeStats }> =>
    apiFetch(`/api/cloze/targeted?prompt=${encodeURIComponent(prompt)}`),

  // Subscriptions (functions/[[all]].ts lines 3999-4232)
  getSubscriptionStatus: (): Promise<SubscriptionStatus> =>
    apiFetch('/api/subscriptions/status'),
  createCheckout: (
    priceId = 'price_pro_monthly',
    billingCycle = 'monthly'
  ): Promise<{ url: string; session_id: string }> =>
    apiFetch('/api/subscriptions/create-checkout', {
      method: 'POST',
      body: JSON.stringify({ price_id: priceId, billing_cycle: billingCycle }),
    }),
  getBillingPortal: (): Promise<{ url: string }> =>
    apiFetch('/api/subscriptions/portal'),
  cancelSubscription: (): Promise<{ success: boolean; message?: string }> =>
    apiFetch('/api/subscriptions/cancel', { method: 'POST' }),
  switchToByok: (): Promise<{ success: boolean; tier: string }> =>
    apiFetch('/api/subscriptions/switch-to-byok', { method: 'POST' }),
  switchToBase: (): Promise<{ success: boolean; tier: string }> =>
    apiFetch('/api/subscriptions/switch-to-base', { method: 'POST' }),
};
